import type { ReactNode } from 'react';
import React from 'react';

import { LuChevronRight } from 'react-icons/lu';

import { cn } from '../../../../utils/cn';
import type { ColorClasses } from '../../../../utils/Colors';
import type { EventBehaviorProps } from '../../../../utils/EventBehavior';
import { handleEventBehavior } from '../../../../utils/EventBehavior';
import { DROPDOWN_SIZE, type DropdownSize } from '../../types';

interface TriggerProps extends EventBehaviorProps {
    'aria-label'?: string;
    'aria-labelledby'?: string;
    id?: string;
    isOpen: boolean;
    displayValue: ReactNode;
    badge?: ReactNode;
    colorClasses: ColorClasses;
    size: DropdownSize;
    roundedTrigger: boolean;
    showBorder: boolean;
    error?: boolean;
    multiple: boolean;
    triggerWidth?: string;
    triggerClassName?: string;
    hideDivider?: boolean;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
}

export const Trigger: React.FC<TriggerProps> = React.forwardRef<HTMLButtonElement, TriggerProps>(
    (
        {
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledBy,
            badge,
            colorClasses,
            disabled = false,
            displayValue,
            error = false,
            eventPreventDefault,
            eventStopPropagation,
            hideDivider = false,
            id,
            isOpen,
            onClick,
            roundedTrigger,
            showBorder,
            size,
            triggerClassName,
            triggerWidth,
        },
        ref,
    ) => (
        <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            onClick={(e) => {
                if (disabled) {
                    return;
                }

                handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
                onClick?.(e);
            }}
            className={cn(
                'flex h-[32px] items-center select-none',
                'pl-3',
                roundedTrigger ? 'rounded-full' : 'rounded-[4px]',
                colorClasses.bg,
                'font-normal font-roboto leading-[1.46]',
                size === DROPDOWN_SIZE.SM ? 'text-sm' : 'text-md',
                colorClasses.text,
                'gap-2',
                'transition-colors',
                showBorder && (error ? 'border border-alert-error' : `border ${colorClasses.border}`),
                triggerClassName,
                disabled && 'cursor-not-allowed bg-surface-light opacity-50',
            )}
            style={triggerWidth ? { width: triggerWidth } : undefined}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
        >
            <span className="flex justify-center items-center whitespace-nowrap overflow-hidden text-ellipsis select-none">
                {displayValue}
            </span>
            {badge}
            <div className="flex items-center ml-auto shrink-0 h-full">
                {!hideDivider && <span className="border-l border-grey-dark h-4/6"></span>}
                <div className={cn('flex-none transition-transform', isOpen && 'rotate-180')}>
                    <LuChevronRight className={cn('h-5 w-8 rotate-90 scale-y-[-100%]', colorClasses.text)} />
                </div>
            </div>
        </button>
    ),
);

Trigger.displayName = 'Trigger';
